import AddAPhotoIcon from "@mui/icons-material/AddAPhoto"
import {
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material"
import { StyledButton } from "components/Button/StyledButton"
import { CameraDialog } from "components/Camera/CameraDialog"
import { ImageSlider } from "components/ImageSlider/ImagesSlider"
import { ResponsiveDialog } from "components/ResponsiveDialog/ResponsiveDialog"
import {
  DeviceEutGroup,
  File,
  FileType,
  FileUploadInput,
  MovementData,
  MovementType,
  User,
} from "graphql/types"
import { useStoreActions } from "hooks/storeHooks"
import { enqueueSnackbar } from "notistack"
import { FormEvent, useEffect, useState } from "react"
import { emptyPerson } from "store/store"
import { useLocalStorage } from "utils/useLocalStorage"
import { v4 } from "uuid"

type EUTMovementDataDialogProp = {
  openDialog: boolean
  onClose: () => void
  eut: DeviceEutGroup
  onSave: (value: MovementData, img: File[]) => void
  eutBarcode: string[]
}

const movementDataEmpty: MovementData = {
  type: MovementType.ENTRANCE,
  date: new Date().toISOString(),
  shipmentNumber: "",
  carrier: "",
  location: "",
  note: "",
  eutIsOk: true,
  packageIsOk: true,
}

export const EUTMovementDataDialog = (props: EUTMovementDataDialogProp) => {
  const { openDialog, onClose, eut, onSave, eutBarcode } = props

  const { uploadFile } = useStoreActions((actions) => actions)

  const [openCameraDialog, setOpenCameraDialog] = useState(false)
  const [newMovementData, setNewMovementData] = useState<MovementData>(movementDataEmpty)
  const [newImages, setNewImages] = useState<File[]>([])

  const [user] = useLocalStorage<User>("user", emptyPerson)

  useEffect(() => {
    if (eutBarcode.length > 1) {
      setNewMovementData({
        ...movementDataEmpty,
        date: new Date().toISOString(),
        type: MovementType.IN_HOUSE,
      })
    } else if (eut.movementData.some((item) => item.type === MovementType.ENTRANCE)) {
      setNewMovementData({
        ...movementDataEmpty,
        date: new Date().toISOString(),
        type: MovementType.IN_HOUSE,
      })
    } else {
      setNewMovementData({
        ...movementDataEmpty,
        date: new Date().toISOString(),
        type: MovementType.ENTRANCE,
      })
    }
  }, [eut.movementData, eutBarcode.length])

  const getImageTitle = () => {
    const text = eutBarcode.join(", ")
    switch (newMovementData.type) {
      case MovementType.ENTRANCE:
        return `Eingang ${text}`
      case MovementType.IN_HOUSE:
        return `Umlagerung ${text}`
      case MovementType.EXIT:
        return `Ausgang ${text}`
    }
  }

  const handleSave = () => {
    onSave(newMovementData, newImages)
    setNewImages([])
    onClose()
  }

  const addImage = async (event: FormEvent<HTMLFormElement>, fotos: string[]) => {
    event.preventDefault()
    if (!fotos.length) {
      enqueueSnackbar("Kein Bild erfasst!", { variant: "error" })
      return
    }

    const formData = new FormData(event.currentTarget)

    const uploadImage: FileUploadInput = {
      id: "NEW",
      filename: v4() + ".png",
      name: formData.get("title") as string,
      base64: fotos[0],
      type: FileType.IMAGE,
      createdAt: new Date().toISOString(),
      createdBy: user.id,
    }

    const imageID = await uploadFile(uploadImage)

    const newImg: File = {
      id: imageID,
      createdAt: uploadImage.createdAt,
      createdBy: uploadImage.createdBy,
      fileURL: "",
      name: uploadImage.name,
      type: uploadImage.type,
      filename: uploadImage.filename,
    }
    setNewImages([...newImages, newImg])
  }

  const isShown = newMovementData.type === MovementType.IN_HOUSE ? false : true

  return (
    <ResponsiveDialog open={openDialog} onClose={onClose}>
      <DialogTitle>EUT Eingangserfassung</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <FormControl size="small" fullWidth sx={{ mt: 1 }}>
          <InputLabel>Bewegungstyp</InputLabel>
          <Select
            label="Bewegungstyp"
            value={newMovementData.type}
            onChange={(e) =>
              setNewMovementData({
                ...newMovementData,
                type: e.target.value as MovementType,
              })
            }
          >
            <MenuItem value={MovementType.ENTRANCE}>Eingang</MenuItem>
            <MenuItem value={MovementType.IN_HOUSE}>Umlagerung</MenuItem>
            <MenuItem value={MovementType.EXIT}>Ausgang</MenuItem>
          </Select>
        </FormControl>

        {isShown && (
          <TextField
            size="small"
            label="Sendungsnummer"
            value={newMovementData.shipmentNumber}
            onChange={(e) =>
              setNewMovementData({
                ...newMovementData,
                shipmentNumber: e.currentTarget.value,
              })
            }
            fullWidth
          />
        )}

        {isShown && (
          <TextField
            size="small"
            label="Spedition"
            value={newMovementData.carrier}
            onChange={(e) =>
              setNewMovementData({
                ...newMovementData,
                carrier: e.currentTarget.value,
              })
            }
            fullWidth
          />
        )}
        <TextField
          size="small"
          label="Lagerplatz"
          value={newMovementData.location}
          onChange={(e) =>
            setNewMovementData({
              ...newMovementData,
              location: e.currentTarget.value,
            })
          }
          fullWidth
        />

        {isShown && (
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!newMovementData.packageIsOk}
                  onChange={(e) =>
                    setNewMovementData({
                      ...newMovementData,
                      packageIsOk: !e.target.checked,
                    })
                  }
                />
              }
              label="Verpackung ist beschädigt"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={!newMovementData.eutIsOk}
                  onChange={(e) =>
                    setNewMovementData({
                      ...newMovementData,
                      eutIsOk: !e.target.checked,
                    })
                  }
                />
              }
              label="EUT ist augenscheinlich beschädigt"
            />
          </FormGroup>
        )}

        <TextField
          size="small"
          label="Notiz / Anmerkung"
          value={newMovementData.note}
          onChange={(e) =>
            setNewMovementData({
              ...newMovementData,
              note: e.currentTarget.value,
            })
          }
          multiline
          minRows={2}
          fullWidth
        />

        <FormControl sx={{ width: "100%", display: "flex", gap: 1 }}>
          <ImageSlider images={newImages} />

          <StyledButton
            buttonType="custom"
            customIcon={<AddAPhotoIcon />}
            customText="Foto hinzufügen"
            onClick={() => setOpenCameraDialog(true)}
            variant="contained"
          />

          <CameraDialog
            onSave={addImage}
            onClose={() => setOpenCameraDialog(false)}
            open={openCameraDialog}
            imageName={getImageTitle()}
          />
        </FormControl>
      </DialogContent>
      <DialogActions>
        <StyledButton buttonType="cancel" onClick={onClose} variant="contained">
          Abbrechen
        </StyledButton>
        <StyledButton buttonType="save" onClick={handleSave} variant="contained">
          Speichern
        </StyledButton>
      </DialogActions>
    </ResponsiveDialog>
  )
}
